'use client';
import React, { useEffect, useRef } from 'react';
import BeforeAfterSlider from './BeforeAfterSlider';

const services = [
  { icon: '🏢', label: 'Commercial Buildings' },
  { icon: '🏠', label: 'Homes' },
  { icon: '🛣️', label: 'Driveways' },
  { icon: '🪵', label: 'Decks' },
  { icon: '🚗', label: 'Vehicles' },
  { icon: '🚐', label: 'RVs' },
  { icon: '🏚️', label: 'Roofs' },
  { icon: '🌿', label: 'Gutters' },
];

export default function HouseWashSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) obs?.observe(sectionRef?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section
      id="house-wash"
      ref={sectionRef}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1520 50%, #0a0f1a 100%)' }}
    >
      {/* Background accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 80% 50%, rgba(59,130,246,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text content */}
          <div>
            <div className="metric-pill mb-5 reveal">House Wash & Property Care</div>
            <h2
              className="font-manrope font-extrabold text-white reveal reveal-delay-1"
              style={{ fontSize: 'clamp(28px, 4vw, 50px)', letterSpacing: '-0.03em', lineHeight: 1.1 }}
            >
              Revitalize your<br />
              <span style={{ color: 'var(--ceramic-blue)' }}>property.</span>
            </h2>
            <p
              className="mt-5 reveal reveal-delay-2"
              style={{ fontSize: '17px', color: 'var(--aluminum-dim)', lineHeight: 1.7, maxWidth: '480px' }}
            >
              We wash and clean everything on your property, leaving it spotless, looking good as new.
            </p>

            {/* Tagline callout */}
            <div
              className="mt-8 reveal reveal-delay-3 inline-flex items-center gap-3 px-5 py-3 rounded-2xl"
              style={{
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(59,130,246,0.25)',
              }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'var(--ceramic-blue)', flexShrink: 0 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span
                className="font-manrope font-bold"
                style={{ fontSize: '14px', color: 'rgba(212,212,216,0.9)', letterSpacing: '-0.01em' }}
              >
                Clean Rides. Clean Home. Clean Vibes.
              </span>
            </div>

            <a
              href="#book"
              className="btn-primary mt-10 reveal reveal-delay-4 inline-flex"
            >
              Book a Property Wash
            </a>
          </div>

          {/* Right: Service tiles bento grid */}
          <div className="reveal reveal-delay-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
              {services?.map((svc, i) => (
                <div
                  key={svc?.label}
                  className={`flex flex-col items-center justify-center gap-2 rounded-2xl p-4 reveal reveal-delay-${(i % 4) + 1}`}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(63,63,70,0.6)',
                    minHeight: '90px',
                    transition: 'border-color 0.2s, background 0.2s',
                  }}
                >
                  <span style={{ fontSize: '26px', lineHeight: 1 }}>{svc?.icon}</span>
                  <span
                    className="font-manrope font-semibold text-center"
                    style={{ fontSize: '12px', color: 'rgba(212,212,216,0.75)', lineHeight: 1.3 }}
                  >
                    {svc?.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Bottom note */}
            <div
              className="mt-4 rounded-2xl px-5 py-4 flex items-start gap-3"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(63,63,70,0.5)' }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="shrink-0 mt-0.5" style={{ color: 'var(--ceramic-blue)' }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p style={{ fontSize: '13px', color: 'var(--aluminum-dim)', lineHeight: 1.6 }}>
                Serving <strong style={{ color: 'rgba(212,212,216,0.9)' }}>Riverview & Moncton</strong>. Contact us for a free property assessment and quote.
              </p>
            </div>
          </div>
        </div>

        {/* Gutters Before & After */}
        <div className="mt-20 reveal">
          <div className="text-center mb-10">
            <div className="metric-pill mb-4 reveal">Before & After</div>
            <h3
              className="font-manrope font-extrabold text-white reveal reveal-delay-1"
              style={{ fontSize: 'clamp(22px, 3vw, 36px)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
            >
              Gutter Cleaning Results
            </h3>
            <p
              className="mt-3 reveal reveal-delay-2"
              style={{ fontSize: '15px', color: 'var(--aluminum-dim)', lineHeight: 1.6 }}
            >
              Drag the slider to see the difference a professional gutter clean makes.
            </p>
          </div>
          <div className="max-w-3xl mx-auto reveal reveal-delay-2">
            <BeforeAfterSlider
              beforeSrc="/assets/images/gutter-1777425371010.jpg"
              afterSrc="/assets/images/gutter-1777425371010.jpg"
              beforeAlt="Gutters clogged with debris, leaves, and dirt buildup before cleaning"
              afterAlt="Clean, clear gutters after professional gutter cleaning service"
              initialPosition={45}
            />
            <p className="mt-3 text-center" style={{ fontSize: '12px', color: 'var(--aluminum-dim)', fontFamily: 'DM Sans, sans-serif' }}>
              ← Drag to reveal the transformation
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
